import { useMutation, useQueryClient } from '@tanstack/react-query';

import BackendService from '../services/backendService';
import { dataURLtoFile } from '../utils/imageCropUtils';
import { createGameImageCacheKey } from './useMediaPreFetcher';

export const useGameMediaFileUploader = (tenantId: string) => {
  const client = useQueryClient();
  const { mutate: upload, error, isPending: isLoading, isSuccess } = useMutation({
    mutationFn: async ({
      imgData,
      fileId,
      svgText,
    }: {
      imgData: string;
      fileId: string;
      svgText?: string;
      onSuccessFunction?: () => void;
    }) => {
      const imgCacheKey = createGameImageCacheKey(fileId);
      client.setQueryData(imgCacheKey, svgText);
      // Update the cache with the new data to show pasted image immediatly.
      if (svgText) {
        client.setQueryData(imgCacheKey, svgText);
      } else if (imgData) {
        client.setQueryData(imgCacheKey, imgData);
      }

      const file = dataURLtoFile(imgData, imgData);

      if (file) {
        const data = await BackendService.instance.getUploadUrlV2({
          pathToFile: `game_files/${fileId}`,
          fileType: 'slideImage',
          mimeType: file?.type || '',
          tenantId,
        });
        const url = data.data.url;

        await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('PUT', url, true);
          xhr.setRequestHeader('Content-Type', file.type);

          xhr.onreadystatechange = function() {
            if (xhr.readyState === 4) {
              if (xhr.status === 200) {
                resolve(xhr.response);
              } else {
                reject(xhr.response);
              }
            }
          };
          xhr.onerror = function(response) {
            reject(response);
          };
          xhr.send(file);
        });
        await BackendService.instance.markUploadSuccessfulV2(
          data.data.uploadId,
          tenantId,
        );
        return Promise.resolve();
      }
      return Promise.reject();
    },

    onSuccess: (_, { onSuccessFunction }) => {
      client.invalidateQueries({
        queryKey: ['uploadedDesignerImage']
      });
      if (onSuccessFunction) {
        onSuccessFunction();
      }
    }
  });

  return {
    upload,
    isUploading: isLoading,
    isSuccess: isSuccess,
    error: error,
  };
};
